import { Injectable, inject } from '@angular/core';
import { PlatformService } from 'ngx-unificator/services';
import { fromEvent, merge } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class FullScreenService {

  private _platform = inject(PlatformService);

  /**
   * Document object
   */
  private _document: any;

  /**
   * Document element
   */
  private _documentEl: any;

  /**
   * Is full screen enabled
   */
  get isFullScreen(): boolean {
    if (this._platform.isBrowser) {
      return !!(this._document.fullscreenElement ||
        this._document.mozFullScreenElement ||
        this._document.webkitFullscreenElement ||
        this._document.msFullscreenElement);
    } else {
      return false;
    }
  }

  /**
   * Open element in full screen mode
   *
   * @param el
   */
  public open(el = this._documentEl) {
    if (el.requestFullscreen) {
      el.requestFullscreen();
    } else if (el.mozRequestFullScreen) { /* Firefox */
      el.mozRequestFullScreen();
    } else if (el.webkitRequestFullscreen) { /* Chrome, Safari and Opera */
      el.webkitRequestFullscreen();
    } else if (el.msRequestFullscreen) { /* IE/Edge */
      el.msRequestFullscreen();
    }
  }

  /**
   * Close full screen mode
   */
  public close() {
    if (!this.isFullScreen) {
      return;
    }
    const document = this._document;
    if (document.exitFullscreen && document.fullscreenEnabled) {
      document.exitFullscreen();
    } else if (document.mozCancelFullScreen) { /* Firefox */
      document.mozCancelFullScreen();
    } else if (document.webkitExitFullscreen) { /* Chrome, Safari and Opera */
      document.webkitExitFullscreen();
    } else if (document.msExitFullscreen) { /* IE/Edge */
      document.msExitFullscreen();
    }
  }

  /**
   * Toggle full screen
   */
  public toggle(el = this._documentEl) {
    if (this.isFullScreen) {
      this.close();
    } else {
      this.open(el);
    }
  }

  /**
   * Emit resize event
   *
   * @private
   */
  private _emitResize() {
    this._document.dispatchEvent(new Event('resize'));
  }

  /**
   * Init internal variables
   *
   * @private
   */
  public init() {
    if (this._platform.isBrowser) {
      this._document = document;
      this._documentEl = document.documentElement;
      this._handleFullScreenChangeEvent();
    }
  }

  /**
   * Emit resize on fullscreenchange event for correct change detection
   *
   * @private
   */
  private _handleFullScreenChangeEvent() {
    merge(
      fromEvent(this._document, 'fullscreenchange'),
      fromEvent(this._document, 'mozfullscreenchange'),
      fromEvent(this._document, 'webkitfullscreenchange'),
      fromEvent(this._document, 'msfullscreenchange'),
    ).subscribe(e => {
      this._emitResize();
      if (this.isFullScreen) {
        this._documentEl.classList.add('full-screen');
      } else {
        this._documentEl.classList.remove('full-screen');
      }
    });
  }

}
